import React, { useState, useEffect } from 'react';
import { Modal, Toast } from 'react-bootstrap';
import { Controller, useWatch } from "react-hook-form";
import Select from 'react-select';
import Api from '../../../Auth/Api';
import { decodeJWT } from '../../jwtUtils';
import { useAjustes } from '../../../Context/AjustesContext';
import { exibirCampoCursosNoFormulario, cursosObrigatoriosNoFormulario, adminPodeVincularCursosAoGestor } from '../../../utils/gestorPermissoes';

const AddUsuarioModal = ({
	showModalAddUsuario,
	setShowModalAddUsuario,
	dataCargos,
	dataUnidades,
	dataCursos,
	customStyles,
	nivel,
	register,
	control,
	errors,
	handleSubmit,
	onSubmitAddParticipante,
	loadingSaveUser,
	selectCursos,
	setSelectCursos,
	setValue,
	setError
}) => {
	const [dataGestores, setDataGestores] = useState([]);
	const [loadingGestores, setLoadingGestores] = useState(false);
	const [showGestorResponsavel, setShowGestorResponsavel] = useState(false);
	const [toast, setToast] = useState({});
	const [showToast, setShowToast] = useState(false);
	const [unidadeGestor, setUnidadeGestor] = useState(null);

	const token = JSON.parse(localStorage.getItem("user_token"));
	const usuarioId = decodeJWT(token).idUsuario;
	const usuarioNome = decodeJWT(token).nomeCompleto;
	const { ajustes } = useAjustes();

	const watchedCargo = useWatch({ control, name: 'cargo' });
	const watchedNivel = useWatch({ control, name: 'nivel' });
	const watchedUnidade = useWatch({ control, name: 'unidade' });
	const watchedUnidadePrincipal = useWatch({ control, name: 'unidade_principal' });

	const syncUnidadePrincipal = (unidadesSelecionadas, principalAtual) => {
		if (!setValue || watchedNivel?.value !== 'Gestor') {
			return;
		}

		if (!unidadesSelecionadas?.length) {
			setValue('unidade_principal', null);
			return;
		}

		if (unidadesSelecionadas.length === 1) {
			setValue('unidade_principal', unidadesSelecionadas[0]);
			return;
		}

		const valores = unidadesSelecionadas.map((item) => item.value);
		if (principalAtual?.value && valores.includes(principalAtual.value)) {
			return;
		}

		setValue('unidade_principal', null);
	};

	useEffect(() => {
		if (watchedNivel?.value === 'Gestor') {
			syncUnidadePrincipal(watchedUnidade, watchedUnidadePrincipal);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [watchedUnidade, watchedNivel?.value]);

	// Função para aplicar máscara de CPF
	const formatCPF = (value) => {
		return value
			.replace(/\D/g, '') // Remove tudo que não é dígito
			.replace(/(\d{3})(\d)/, '$1.$2') // Coloca ponto após os 3 primeiros dígitos
			.replace(/(\d{3})(\d)/, '$1.$2') // Coloca ponto após os 3 segundos dígitos
			.replace(/(\d{3})(\d{1,2})$/, '$1-$2'); // Coloca hífen antes dos 2 últimos dígitos
	};

	// Função para validar CPF
	const validarCPF = (cpf) => {
		cpf = cpf.replace(/\D/g, '');

		if (cpf.length !== 11) return false;

		// Verifica se todos os dígitos são iguais
		if (/^(\d)\1{10}$/.test(cpf)) return false;

		// Validação do primeiro dígito verificador
		let soma = 0;
		for (let i = 0; i < 9; i++) {
			soma += parseInt(cpf.charAt(i)) * (10 - i);
		}
		let resto = 11 - (soma % 11);
		let digitoVerificador1 = resto === 10 || resto === 11 ? 0 : resto;

		if (digitoVerificador1 !== parseInt(cpf.charAt(9))) return false;

		// Validação do segundo dígito verificador
		soma = 0;
		for (let i = 0; i < 10; i++) {
			soma += parseInt(cpf.charAt(i)) * (11 - i);
		}
		resto = 11 - (soma % 11);
		let digitoVerificador2 = resto === 10 || resto === 11 ? 0 : resto;

		if (digitoVerificador2 !== parseInt(cpf.charAt(10))) return false;

		return true;
	};

	// Buscar gestores apenas se o usuário for Administrador ou RH
	useEffect(() => {
		if (nivel === 'Administrador' || nivel === 'RH') {
			const fetchGestores = async () => {
				setLoadingGestores(true);
				try {
					const token = JSON.parse(localStorage.getItem("user_token"));
					const response = await Api.get('api/show/gestores', {
						headers: {
							Authorization: `Bearer ${token}`
						}
					});

					if (response.data) {
						setDataGestores(response.data.map(gestor => ({
							value: gestor.id,
							label: gestor.nome_completo
						})));
					}
				} catch (error) {
					console.error('Erro ao buscar gestores:', error);
					showToastMessage('Erro!', 'Não foi possível carregar a lista de gestores');
				} finally {
					setLoadingGestores(false);
				}
			};

			fetchGestores();
		} else if (nivel === 'Gestor') {
			// Para Gestor, criar lista com apenas o próprio gestor
			setDataGestores([{
				value: usuarioId,
				label: usuarioNome
			}]);
		}
	}, [nivel, usuarioId, usuarioNome]);

	// Buscar unidade do gestor logado quando o modal abrir
	useEffect(() => {
		if (nivel === 'Gestor' && showModalAddUsuario) {
			const fetchUnidadeGestor = async () => {
				try {
					const token = JSON.parse(localStorage.getItem("user_token"));
					const response = await Api.get('api/meu-perfil', {
						headers: {
							Authorization: `Bearer ${token}`
						}
					});
					if (response.data && response.data.unidade) {
						setUnidadeGestor(response.data.unidade);
					}
				} catch (error) {
					console.error('Erro ao buscar unidade do gestor:', error);
				}
			};
			fetchUnidadeGestor();
		}
	}, [nivel, showModalAddUsuario]);

	// Pré-selecionar gestor responsável quando for Gestor e tipo for Aluno
	useEffect(() => {
		if (nivel === 'Gestor' && showGestorResponsavel && setValue) {
			const gestorLogado = {
				value: usuarioId,
				label: usuarioNome
			};
			setValue('gestor_responsavel', gestorLogado);
		} else if (nivel === 'Gestor' && !showGestorResponsavel && setValue) {
			// Limpar o campo quando não for mais Aluno
			setValue('gestor_responsavel', null);
		}
	}, [nivel, showGestorResponsavel, usuarioId, usuarioNome, setValue]);

	// Pré-selecionar unidade do gestor quando for Gestor e modal estiver aberto
	useEffect(() => {
		if (nivel === 'Gestor' && showModalAddUsuario && unidadeGestor && dataUnidades && dataUnidades.length > 0 && setValue) {
			// Pequeno delay para garantir que o reset já foi executado
			const timer = setTimeout(() => {
				// Buscar a unidade pelo nome (label)
				const unidadeEncontrada = dataUnidades.find(item => item.label === unidadeGestor);
				if (unidadeEncontrada) {
					const unidadeFormatada = {
						value: unidadeEncontrada.label,
						label: unidadeEncontrada.label
					};
					setValue('unidade', unidadeFormatada);
				}
			}, 100);
			
			return () => clearTimeout(timer);
		}
	}, [nivel, showModalAddUsuario, unidadeGestor, dataUnidades, setValue]);

	// Limpar estados e campos quando o modal fechar
	useEffect(() => {
		if (!showModalAddUsuario) {
			// Limpar estados locais
			setShowGestorResponsavel(false);
			setSelectCursos(false);
			
			// Limpar campos do formulário que não devem persistir
			if (setValue) {
				setValue('nivel', null);
				setValue('gestor_responsavel', null);
			}
		}
	}, [showModalAddUsuario, setValue, setSelectCursos]);

	useEffect(() => {
		const carregarCursosDoCargo = async () => {
			if (watchedNivel?.value !== 'Aluno' || !watchedCargo?.value || !setValue) {
				return;
			}

			try {
				const response = await Api.get(`api/cargos/${watchedCargo.value}/cursos`, {
					headers: { Authorization: `Bearer ${token}` }
				});

				const cursosCargo = (response.data || []).map((curso) => ({
					value: curso.id,
					label: curso.nome
				}));

				setValue('cursos', cursosCargo);
				setSelectCursos(true);
			} catch (error) {
				console.error('Erro ao buscar cursos do cargo:', error);
			}
		};

		carregarCursosDoCargo();
	}, [watchedCargo?.value, watchedNivel?.value, setValue, setSelectCursos, token]);

	const getDataNiveis = (nivel) => {
		switch (nivel) {
			case 'Administrador':
				return [
					{ value: 'Aluno', label: 'Aluno' },
					{ value: 'Gestor', label: 'Gestor' },
					{ value: 'Administrador', label: 'Administrador' },
					{ value: 'RH', label: 'RH' }
				];
			case 'Gestor':
				return [
					{ value: 'Aluno', label: 'Aluno' },
				];
			case 'RH':
				return [
					{ value: 'Aluno', label: 'Aluno' },
					{ value: 'Gestor', label: 'Gestor' },
				];
			default:
				return [];
		}
	};

	const dataNiveis = getDataNiveis(nivel);

	// Função para mostrar mensagens de toast
	const showToastMessage = (header, body, variant = 'error') => {
		const type = variant === 'success' ? 'toast-success' : variant === 'danger' ? 'toast-error' : 'toast-' + variant;
		setToast({ header, body, type });
		setShowToast(true);
	};

	const onSubmit = async (data) => {
		// Se o nível selecionado for Aluno
		if (data.nivel?.value === 'Aluno') {
			// Se o usuário logado for Gestor e não tiver selecionado gestor responsável
			if (nivel === 'Gestor' && !data.gestor_responsavel) {
				data.gestor_responsavel = {
					value: usuarioId,
					label: usuarioNome
				};
			}
		} else {
			// Para outros níveis, remover gestor responsável caso tenha sido selecionado
			delete data.gestor_responsavel;
		}

		const result = await onSubmitAddParticipante(data);

		if (result && result.success) {
			showToastMessage('Sucesso!', result.message, 'success');
			setShowModalAddUsuario(false);
		} else if (result) {
			showToastMessage('Erro!', result.message, 'danger');
		}
	};

	return (
		<>
			<Toast
				show={showToast}
				onClose={() => setShowToast(false)}
				delay={4000}
				autohide
				className={`position-fixed top-0 end-0 m-3 toast-icon ${toast.type || 'toast-error'}`}
				style={{ zIndex: 1060 }}
			>
				<Toast.Header>
					<strong className="me-auto">{toast.header}</strong>
				</Toast.Header>
				<Toast.Body>{toast.body}</Toast.Body>
			</Toast>

			<Modal size="lg" show={showModalAddUsuario} onHide={() => setShowModalAddUsuario(false)} backdrop="static">
				<form onSubmit={handleSubmit(onSubmit)}>
					<Modal.Header closeButton>
						<span className="fw-semibold fs-5 text-primary"> Adicionar Usuário </span>
					</Modal.Header>
					<Modal.Body className="bg-gray-100 p-4">
						<div className="container">
							<div className="row">
								<div className="col-md-12">
									<div className="body">
										<div className="d-flex">
											<div className="d-flex flex-column w-100">
												<div className="row mb-3">
													<div className="col-12">
														<div className="mb-3">
															<label className="form-label text-primary fw-medium">Nome</label>
															<input
																type="text"
																{...register("nome_completo", { required: true })}
																placeholder="Nome do usuário"
																className="form-control border-new-light"
															/>
															{errors.nome_completo && <span className='text-danger fs-7'>Nome completo é obrigatório.</span>}
														</div>
													</div>

													<div className="col-12 col-md-6">
														<div className="mb-3">
															<label className="form-label text-primary fw-medium">CPF</label>
															<Controller
																name="cpf"
																control={control}
																rules={{
																	required: nivel !== 'Administrador' ? 'CPF é obrigatório' : false,
																	validate: (value) => {
																		const cpfLimpo = value?.replace(/\D/g, '');
																		// Se for Administrador e CPF estiver vazio, permite
																		if (nivel === 'Administrador' && !cpfLimpo) {
																			return true;
																		}
																		if (!cpfLimpo || cpfLimpo.length !== 11) {
																			return 'CPF deve ter 11 dígitos';
																		}
																		if (!validarCPF(value)) {
																			return 'CPF inválido';
																		}
																		return true;
																	}
																}}
																render={({ field: { onChange, value } }) => (
																	<input
																		type="text"
																		value={value || ''}
																		onChange={(e) => {
																			const formatted = formatCPF(e.target.value);
																			onChange(formatted);
																		}}
																		placeholder="000.000.000-00"
																		className="form-control border-new-light"
																		maxLength={14}
																	/>
																)}
															/>
															{errors.cpf && <span className='text-danger fs-7'>{errors.cpf.message}</span>}
														</div>
													</div>

													<div className="col-12 col-md-6">
														<div className="mb-3">
															<label className="form-label text-primary fw-medium">Matrícula</label>
															<input
																type="number"
																{...register("codigo", { required: true })}
																placeholder="Digite a matrícula"
																className="form-control border-new-light"
															/>
															{errors.codigo && <span className='text-danger fs-7'>Matrícula é obrigatório.</span>}
														</div>
													</div>

													<div className="col-12">
														<div className="mb-3">
															<label className="form-label text-primary fw-medium">Email</label>
															<input
																type="email"
																{...register("email", { required: true })}
																placeholder="email@email.com"
																className="form-control border-new-light"
															/>
															{errors.email && <span className='text-danger fs-7'>Email é obrigatório.</span>}
														</div>
													</div>

													<div className="col-12 col-md-6">
														<div className="mb-3">
															<label className="form-label text-primary fw-medium">Cargo</label>
															<Controller
																name="cargo"
																control={control}
																defaultValue={[]}
																rules={{ required: true }}
																render={({ field }) => (
																	<Select
																		{...field}
																		options={dataCargos}
																		value={field.value}
																		styles={customStyles}
																		placeholder="Selecione um cargo"
																		noOptionsMessage={() => "Nenhum cargo encontrado."}
																	/>
																)}
															/>
															{errors.cargo && <span className='text-danger fs-7'>Cargo é obrigatório.</span>}
														</div>
													</div>

													<div className="col-12 col-md-6">
														<div className="mb-3">
															<label className="form-label text-primary fw-medium">Tipo Usuário</label>
															<Controller
																name="nivel"
																control={control}
																defaultValue={[]}
																rules={{ required: true }}
																render={({ field }) => (
																	<Select
																		{...field}
																		options={dataNiveis}
																		value={field.value}
																		styles={customStyles}
																		onChange={value => {
																			if (value.value === 'Aluno') {
																				setSelectCursos(true);
																				setShowGestorResponsavel(true);
																			} else if (value.value === 'Gestor' && adminPodeVincularCursosAoGestor(ajustes, nivel)) {
																				setSelectCursos(true);
																				setShowGestorResponsavel(false);
																			} else {
																				setSelectCursos(false);
																				setShowGestorResponsavel(false);
																			}

																			if (value.value === 'Gestor') {
																				setValue('unidade', []);
																				setValue('unidade_principal', null);
																			} else if (watchedNivel?.value === 'Gestor') {
																				setValue('unidade', null);
																				setValue('unidade_principal', null);
																			}

																			field.onChange(value);
																		}}
																		placeholder="Selecione tipo do usuário"
																		noOptionsMessage={() => "Nenhum tipo de usuário encontrado."}
																	/>
																)}
															/>
															{errors.nivel && <span className='text-danger fs-7'>Tipo de usuário é obrigatório.</span>}
														</div>
													</div>

													<div className="col-12 col-md-6">
														<div className="mb-3">
															<label className="form-label text-primary fw-medium">
																Unidade{watchedNivel?.value === 'Gestor' ? 's' : ''}
															</label>
															<Controller
																name="unidade"
																control={control}
																defaultValue={watchedNivel?.value === 'Gestor' ? [] : null}
																rules={{
																	required: 'Selecione ao menos uma unidade',
																	validate: (value) => {
																		if (watchedNivel?.value === 'Gestor') {
																			return (Array.isArray(value) && value.length > 0) || 'Selecione ao menos uma unidade';
																		}
																		return value?.value ? true : 'Selecione uma unidade';
																	}
																}}
																render={({ field }) => (
																	<Select
																		{...field}
																		isMulti={watchedNivel?.value === 'Gestor'}
																		options={dataUnidades.map((value) => ({
																			value: value.label,
																			label: `${value.label}`,
																		}))}
																		value={field.value}
																		styles={customStyles}
																		onChange={(value) => {
																			const newValue = value || (watchedNivel?.value === 'Gestor' ? [] : null);
																			field.onChange(newValue);
																			if (watchedNivel?.value === 'Gestor') {
																				syncUnidadePrincipal(newValue, watchedUnidadePrincipal);
																			}
																		}}
																		placeholder={watchedNivel?.value === 'Gestor' ? 'Selecione as unidades' : 'Selecione uma unidade'}
																		noOptionsMessage={() => "Nenhuma unidade encontrada."}
																	/>
																)}
															/>
															{errors.unidade && <span className='text-danger fs-7'>{errors.unidade.message}</span>}
														</div>
													</div>

													{watchedNivel?.value === 'Gestor' && Array.isArray(watchedUnidade) && watchedUnidade.length >= 2 && (
														<div className="col-12 col-md-6">
															<div className="mb-3">
																<label className="form-label text-primary fw-medium">Unidade principal</label>
																<Controller
																	name="unidade_principal"
																	control={control}
																	defaultValue={null}
																	rules={{
																		required: 'Selecione a unidade principal',
																		validate: (value) => {
																			if (!value?.value) {
																				return 'Selecione a unidade principal';
																			}
																			const valores = (watchedUnidade || []).map((item) => item.value);
																			return valores.includes(value.value) || 'A unidade principal deve estar entre as unidades selecionadas';
																		}
																	}}
																	render={({ field }) => (
																		<Select
																			{...field}
																			options={(watchedUnidade || []).map((item) => ({
																				value: item.value,
																				label: item.label,
																			}))}
																			value={field.value}
																			styles={customStyles}
																			onChange={(value) => field.onChange(value || null)}
																			placeholder="Selecione a unidade principal"
																			noOptionsMessage={() => "Nenhuma unidade selecionada."}
																		/>
																	)}
																/>
																{errors.unidade_principal && <span className='text-danger fs-7'>{errors.unidade_principal.message}</span>}
															</div>
														</div>
													)}

													{/* Campo de Gestor Responsável - apenas para nível Aluno */}
													{showGestorResponsavel && (nivel === 'Administrador' || nivel === 'RH' || nivel === 'Gestor') && (
														<div className="col-12 col-md-6">
															<div className="mb-3">
																<label className="form-label text-primary fw-medium">Gestor Responsável</label>
																<Controller
																	name="gestor_responsavel"
																	control={control}
																	defaultValue={nivel === 'Gestor' ? { value: usuarioId, label: usuarioNome } : null}
																	render={({ field }) => (
																		<Select
																			{...field}
																			options={dataGestores}
																			value={field.value}
																			styles={customStyles}
																			isLoading={loadingGestores}
																			isDisabled={nivel === 'Gestor'}
																			onChange={async (value) => {
																				field.onChange(value);
																				
																				// Se for RH ou Administrador, buscar e pré-selecionar a unidade do gestor
																				if ((nivel === 'Administrador' || nivel === 'RH') && value && value.value && dataUnidades && dataUnidades.length > 0 && setValue) {
																					try {
																						const token = JSON.parse(localStorage.getItem("user_token"));
																						const response = await Api.get(`api/usuario/${value.value}`, {
																							headers: {
																								Authorization: `Bearer ${token}`
																							}
																						});
																						
																						if (response.data && response.data.unidade) {
																							// Buscar a unidade pelo nome (label)
																							const unidadeEncontrada = dataUnidades.find(item => item.label === response.data.unidade);
																							if (unidadeEncontrada) {
																								const unidadeFormatada = {
																									value: unidadeEncontrada.label,
																									label: unidadeEncontrada.label
																								};
																								setValue('unidade', unidadeFormatada);
																							}
																						}
																					} catch (error) {
																						console.error('Erro ao buscar unidade do gestor:', error);
																					}
																				}
																			}}
																			placeholder="Selecione o gestor responsável"
																			noOptionsMessage={() => "Nenhum gestor encontrado."}
																		/>
																	)}
																/>
															</div>
														</div>
													)}

													{exibirCampoCursosNoFormulario(ajustes, nivel, watchedNivel) && (
														<div className="col-12 col-md-12">
															<div className="mb-3">
																<label className="form-label text-primary fw-medium">
																	{watchedNivel?.value === 'Gestor' ? 'Cursos vinculados ao gestor' : 'Cursos'}
																</label>
																<Controller
																	name="cursos"
																	control={control}
																	defaultValue={[]}
																	rules={{ required: cursosObrigatoriosNoFormulario(ajustes, nivel, watchedNivel) }}
																	render={({ field }) => (
																		<Select
																			{...field}
																			isMulti
																			options={dataCursos.map((value) => ({
																				value: value.id,
																				label: `${value.nome}`,
																			}))}
																			value={field.value}
																			styles={customStyles}
																			onChange={value => field.onChange(value)}
																			noOptionsMessage={() => "Nenhum curso encontrado."}
																			placeholder="Selecione os cursos"
																		/>
																	)}
																/>
																{errors.cursos && <span className='text-danger fs-7'>Selecione pelo menos um curso.</span>}
															</div>
														</div>
													)}
													<div className="row p-0 m-0">
														<div className="col-12 col-md-6">
															<div className="mb-3">
																<label className="form-label text-primary fw-medium">Senha</label>
																<input
																	type="password"
																	{...register("senha", { required: true })}
																	placeholder="********"
																	className="form-control border-new-light"
																/>
																{errors.senha && <span className='text-danger fs-7'>Senha é obrigatória.</span>}
															</div>
														</div>

														<div className="col-12 col-md-6">
															<div className="mb-3">
																<label className="form-label text-primary fw-medium">Confirme a senha</label>
																<input
																	type="password"
																	{...register("confirme_senha", { required: true })}
																	placeholder="********"
																	className="form-control border-new-light"
																/>
																{errors.confirme_senha && <span className='text-danger fs-7'>Confirme a senha.</span>}
															</div>
														</div>
													</div>
												</div>
											</div>
										</div>
									</div>
								</div>
							</div>
						</div>
					</Modal.Body>
					<Modal.Footer className="justify-content-end">
						<div className="d-flex justify-content-end">
							<button
								type="button"
								className="btn me-2"
								onClick={() => setShowModalAddUsuario(false)}
								disabled={loadingSaveUser}
							>
								Cancelar
							</button>
							<button
								type="submit"
								className="btn btn-primary"
								disabled={loadingSaveUser}
							>
								{loadingSaveUser ? (
									<>
										<span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
										Salvando...
									</>
								) : (
									'Salvar'
								)}
							</button>
						</div>
					</Modal.Footer>
				</form>
			</Modal>
		</>
	);
};

export default AddUsuarioModal; 